import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
// Define the PaymentOrder interface based on API response
interface PaymentOrder {
  id: string;
  orderId: string;
  type: string;
  billTo: string;
  office: string;
  agent: string;
  orderDate: string;
  dueDate: string;
  status: string;
  original: number;
  paid: number;
  balance: number;
  aging: string;
}

interface PaymentOrdersProps {
  ordersData?: any[];
  onSelectionChange?: (selectedOrders: {
    count: number;
    totalBalance: number;
    orders: SelectedOrder[];
  }) => void;
  pagination?: { pageIndex: number; pageSize: number };
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  totalCount?: number;
  totalPages?: number;
}

interface SelectedOrder {
  order_id: string;
  order_type: string;
  order_number?: string;
  bill_to?: string;
  amount?: number;
}

const columnHelper = createColumnHelper<PaymentOrder>();

const splitBillToValues = (billTo: string) =>
  String(billTo || "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

const BillToCell: React.FC<{ value: string }> = ({ value }) => {
  const billToValues = splitBillToValues(value);
  if (billToValues.length === 0) return <div className="text-sm fw-bold">—</div>;

  const visibleValues = billToValues.slice(0, 2);
  const remainingValues = billToValues.slice(2);

  return (
    <div className="text-sm fw-bold d-flex align-items-center flex-wrap gap-1">
      <span>{visibleValues.join(", ")}</span>
      {remainingValues.length > 0 && (
        <span
          className="badge rounded-pill bg-body-tertiary text-secondary-emphasis border"
          title={remainingValues.join("\n")}
          aria-label={`Remaining bill-to values: ${remainingValues.join(", ")}`}
        >
          +{remainingValues.length}
        </span>
      )}
    </div>
  );
};

const PaymentOrders: React.FC<PaymentOrdersProps> = ({ 
  ordersData = [], 
  onSelectionChange,
  pagination: externalPagination,
  onPaginationChange,
  totalCount = 0,
  totalPages = 0
}) => {
  const [data, setData] = useState<PaymentOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(externalPagination || { pageIndex: 0, pageSize: 10 });
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const prevSelectionRef = useRef<{ count: number; totalBalance: number; orders: any[] } | null>(null);

  // Sync external pagination with internal state
  useEffect(() => {
    if (externalPagination) {
      setPagination(externalPagination);
    }
  }, [externalPagination]);

  // Transform API data to PaymentOrder format
  useEffect(() => {
    if (ordersData && ordersData.length > 0) {
      setLoading(true);
      
      // Transform API response to PaymentOrder format
      const transformedData: PaymentOrder[] = ordersData.map((order: any, index: number) => ({
        id: order.order_id,
        orderId: order.order_number || order.order_id,
        type: order.order_type || "Sales",
        billTo: order.bill_to || "—", // Not provided in API response
        office: order.office?.name || "—",
        agent: order.agent?.name || "—",
        orderDate: order.order_date ? new Date(order.order_date).toLocaleDateString() : "—",
        dueDate: order.due_date ? new Date(order.due_date).toLocaleDateString() : "—",
        status: order.status || "Unpaid",
        original: parseFloat(order.original || 0),
        paid: parseFloat(order.paid || 0),
        balance: parseFloat(order.balance || 0),
        aging: order.aging_days?.toString() || "0"
      }));
      
      setData(transformedData);
      setLoading(false);
    } else {
      setData([]);
      setLoading(false);
    }
  }, [ordersData]);

  // Handle individual row selection
  const handleRowSelect = (rowId: string, checked: boolean) => {
    const newSelectedRows = new Set(selectedRows);
    if (checked) {
      newSelectedRows.add(rowId);
    } else {
      newSelectedRows.delete(rowId);
    }
    setSelectedRows(newSelectedRows);
  };

  // Define table columns using TanStack Table
  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "select",
        header: () => (
          <div className="th-text">
            <input
              type="checkbox"
              className="form-check-input"
              onChange={(e) => {
                // Handle select all functionality
                if (e.target.checked) {
                  // Select all rows on current page
                  const currentPageRows = table.getRowModel().rows;
                  const newSelectedRows = new Set(selectedRows);
                  currentPageRows.forEach((row: any) => {
                    newSelectedRows.add(row.original.id);
                  });
                  setSelectedRows(newSelectedRows);
                } else {
                  // Deselect all rows on current page
                  const currentPageRows = table.getRowModel().rows;
                  const newSelectedRows = new Set(selectedRows);
                  currentPageRows.forEach((row: any) => {
                    newSelectedRows.delete(row.original.id);
                  });
                  setSelectedRows(newSelectedRows);
                }
              }}
              ref={(el) => {
                if (el) {
                  const currentPageRows = table.getRowModel().rows;
                  const allSelected = currentPageRows.length > 0 && currentPageRows.every((row: any) => selectedRows.has(row.original.id));
                  const someSelected = currentPageRows.some((row: any) => selectedRows.has(row.original.id));
                  el.checked = allSelected;
                  el.indeterminate = someSelected && !allSelected;
                }
              }}
            />
          </div>
        ),
        cell: (info) => (
          <input
            type="checkbox"
            className="form-check-input"
            checked={selectedRows.has(info.row.original.id)}
            onChange={(e) => handleRowSelect(info.row.original.id, e.target.checked)}
          />
        ),
      }),

      columnHelper.accessor("orderId", {
        id: "orderId",
        header: () => <div className="th-text">Order ID</div>,
        cell: (info) => (
          <div className="fw-bold fs-16" style={{ color: '#2563eb' }}>
            {info.getValue()}
          </div>
        ),
      }),

      columnHelper.accessor("type", {
        id: "type",
        header: () => <div className="th-text">Type</div>,
        cell: (info) => {
          const getTypeStyle = (type: string) => {
            switch (type.toLowerCase()) {
              case "sales":
                return {
                  backgroundColor: "#dbeafe",
                  color: "#193cb8"
                };
              case "installation":
                return {
                  backgroundColor: "#f3e8ff",
                  color: "#6e11b0"
                };
              case "removal":
                return {
                  backgroundColor: "#ffedd4",
                  color: "#9f2d00"
                };
              default:
                return {
                  backgroundColor: "#f6f3f4",
                  color: "#1e2939"
                };
            }
          };
          return (
            <div className="text-sm">
              <span 
                className="badge rounded-pill px-2 py-1 fw-bold fs-12"
                style={getTypeStyle(info.getValue())}
              >
                {info.getValue()}
              </span>
            </div>
          );
        },
      }),

      columnHelper.accessor("billTo", {
        id: "billTo",
        header: () => <div className="th-text">Bill-To</div>,
        cell: (info) => <BillToCell value={info.getValue()} />,
      }),

      columnHelper.accessor("office", {
        id: "office",
        header: () => <div className="th-text">Office</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
      }),

      columnHelper.accessor("agent", {
        id: "agent",
        header: () => <div className="th-text">Agent</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
      }),

      columnHelper.accessor("orderDate", {
        id: "orderDate",
        header: () => <div className="th-text">Order Date</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
      }),

      columnHelper.accessor("dueDate", {
        id: "dueDate",
        header: () => <div className="th-text">Due Date</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
      }),

      columnHelper.accessor("status", {
        id: "status",
        header: () => <div className="th-text">Status</div>,
        cell: (info) => {
          const getStatusStyle = (status: string) => {
            switch (status.toLowerCase()) {
              case "pending":
                return {
                  backgroundColor: "#ffedd4",
                  color: "#9f2d00"
                };
              case "partial payment":
                return {
                  backgroundColor: "#f3e8ff",
                  color: "#6e11b0"
                };
              case "paid":
                return {
                  backgroundColor: "#d1fae5",
                  color: "#065f46"
                };
              case "hold":
                return {
                  backgroundColor: "#f6f3f4",
                  color: "#1e2939"
                };
              default:
                return {
                  backgroundColor: "#f6f3f4",
                  color: "#1e2939"
                };
            }
          };
          return (
            <div className="text-sm">
              <span 
                className="badge rounded-pill px-2 py-1 fw-bold fs-12"
                style={getStatusStyle(info.getValue())}
              >
                {info.getValue()}
              </span>
            </div>
          );
        },
      }),

      columnHelper.accessor("original", {
        id: "original",
        header: () => <div className="th-text">Original</div>,
        cell: (info) => (
          <div className="text-sm fw-bold">${Number(info.getValue()).toFixed(2)}</div>
        ),
      }),

      columnHelper.accessor("paid", {
        id: "paid",
        header: () => <div className="th-text">Paid</div>,
        cell: (info) => (
          <div className={`text-sm fw-bold`} style={{ color: "#00a63e" }}>
            ${Number(info.getValue()).toFixed(2)}
          </div>
        ),
      }),

      columnHelper.accessor("balance", {
        id: "balance",
        header: () => <div className="th-text">Balance Due</div>,
        cell: (info) => (
          <div className="text-sm fw-bold">${Number(info.getValue()).toFixed(2)}</div>
        ),
      }),

      // columnHelper.accessor("aging", {
      //   id: "aging",
      //   header: () => <div className="th-text">Aging</div>,
      //   cell: (info) => (
      //     <div className="text-sm">{info.getValue()}</div>
      //   ),
      // }),
    ];
  }, [selectedRows]);

  const handlePaginationChange = (updater: any) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
    setPagination(newPagination);
    if (onPaginationChange) {
      onPaginationChange(newPagination);
    }
  };

  const table = useReactTable({
    data,
    columns,
    pageCount: totalPages || Math.ceil(totalCount / pagination.pageSize),
    state: {
      pagination,
    },
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
  });

  // Calculate selected orders summary
  const selectedOrdersSummary = useMemo(() => {
    const selectedOrders = data.filter(order => selectedRows.has(order.id));
    const totalBalance = selectedOrders.reduce((sum, order) => sum + order.balance, 0);
    const totalOriginal = selectedOrders.reduce((sum, order) => sum + order.original, 0);
    const totalPaid = selectedOrders.reduce((sum, order) => sum + order.paid, 0);
    
    return {
      count: selectedOrders.length,
      totalBalance,
      totalOriginal,
      totalPaid,
    };
  }, [data, selectedRows]);

  // Notify parent component when selection changes
  useEffect(() => {
    if (onSelectionChange) {
      const selectedOrders = data.filter(order => selectedRows.has(order.id));
      const totalBalance = selectedOrders.reduce((sum, order) => sum + order.balance, 0);
      
      const currentSelection = {
        count: selectedOrders.length,
        totalBalance,
        orders: selectedOrders.map(order => ({
          order_id: order.id,
          order_type: order.type,
          order_number: order.orderId,
          bill_to: order.billTo,
          amount: order.balance
        }))
      };
      
      // Only call onSelectionChange if the selection actually changed
      const prevSelection = prevSelectionRef.current;
      if (!prevSelection || 
          prevSelection.count !== currentSelection.count ||
          prevSelection.totalBalance !== currentSelection.totalBalance ||
          JSON.stringify(prevSelection.orders) !== JSON.stringify(currentSelection.orders)) {
        
        console.log('PaymentOrders: Selection changed', {
          selectedRows: Array.from(selectedRows),
          selectedOrders: selectedOrders.length,
          totalBalance
        });
        
        prevSelectionRef.current = currentSelection;
        onSelectionChange(currentSelection);
      }
    }
  }, [selectedRows, data, onSelectionChange]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="ms-2">Loading payment orders...</span>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0">Payment Orders</h5>
          <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2 fs-13">
            {data.length} orders
          </span>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop shadow-sm">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="orderId"
            titleColId="orderId"
            actionColId="select"
          />
        </div>
      </div>
      
      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          No records found
        </div>
      ) : (
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      )}
    </div>
  );
};

export default PaymentOrders;
